import { CREDENTIALS } from '../constants';
import { createRequest, getUserId } from '../utils/hmac-utils';

const REVAMP_BASE = (process.env.REACT_APP_API_ENDPOINT_REVAMP || '').replace(/\/$/, '');

function getRevampToken(passedToken?: string | null): string | null {
  if (passedToken && typeof passedToken === 'string' && passedToken.trim()) return passedToken.trim();
  try {
    const raw = localStorage.getItem(CREDENTIALS);
    if (!raw) return null;
    const stored = JSON.parse(raw);
    const cred = stored?.credentials ?? stored;
    const token =
      cred?.accessToken ??
      cred?.access_token ??
      cred?.token ??
      cred?.idToken ??
      cred?.id_token ??
      cred?.jwtToken ??
      cred?.jwt;
    return token && typeof token === 'string' && token.trim() ? token.trim() : null;
  } catch {
    return null;
  }
}

function authHeaders(token?: string | null): Record<string, string> {
  const t = getRevampToken(token);
  return {
    'Content-Type': 'application/json',
    'X-source': 'MP',
    ...(t ? { Authorization: `Bearer ${t}` } : {}),
  };
}

async function buildHmacBody(data: Record<string, unknown>): Promise<Record<string, unknown>> {
  try {
    const userId = getUserId();
    const bodyWithUser = userId ? { ...data, userId: userId } : data;
    return await createRequest(bodyWithUser);
  } catch {
    // proceed with original body if HMAC key is not yet initialized
    return data;
  }
}

// ── Offer API types ──

export interface ApiPromoCode {
  promoCodeId?: string;
  // legacy snake_case fields (kept for backward compat)
  promo_code_id?: string;
  code?: string;
  name?: string;          // real API uses "name" as the promo code string
  description?: string;
  offer_id?: string;
  offerId?: string;
  effective_from?: string | null;
  effectiveFrom?: string | null;
  effective_until?: string | null;
  effectiveUntil?: string | null;
  valid_from?: string | null;
  valid_to?: string | null;
  single_use?: boolean;
  singleUse?: boolean;
  max_redemptions?: number | null;
  max_usage?: number | null;
  usage_count?: number;
  usageCount?: number;
  status?: string;
  created_at?: string;
  createdAt?: string;
}

export interface ApiItemEligibility {
  mode?: 'ALL' | 'INCLUDE' | 'EXCLUDE';
  itemIds?: string[];
}

/** Nested BOGO config from GET offer(s) — `buyItems` / `getItems` use catalog item IDs. */
export interface ApiBogoItemRef {
  /** Junction row UUID (send in `deleteFields.bogoItemIds` when removed on edit). */
  id?: string;
  itemId?: string;
  item_id?: string;
}

export interface ApiBogoBlock {
  bogoConfigId?: string;
  buyItems?: ApiBogoItemRef[];
  buy_items?: ApiBogoItemRef[];
  buyQty?: number;
  buy_qty?: number;
  getItems?: ApiBogoItemRef[];
  get_items?: ApiBogoItemRef[];
  getQty?: number;
  get_qty?: number;
  getDiscountPercent?: number;
  get_discount_percent?: number;
  equalOrLesserValue?: boolean;
  equal_or_lesser_value?: boolean;
  excludeItemIds?: string[];
  exclude_item_ids?: string[];
}

export interface ApiOffer {
  // Real API fields
  offerId?: string;
  locationId?: string;
  offerType?: string;
  applicationLevel?: number;
  autoApply?: boolean;
  maxCap?: number | null;
  noOfFreeItems?: number | null;
  maxDiscountCap?: number | null;
  max_discount_cap?: number | null;
  minSubtotal?: number | null;
  isExclusive?: boolean;
  isOfferVisible?: boolean;
  isHidden?: boolean;
  isFirstTimeOnly?: boolean;
  requiresManagerApproval?: boolean;
  startDate?: string | null;
  endDate?: string | null;
  orderTypeIds?: string[];
  itemEligibility?: ApiItemEligibility;
  rules?: unknown;
  promoCodes?: ApiPromoCode[];
  per_customer_limit?: number | null;
  customer_period?: string | null;
  perCustomerLimit?: number | null;
  customerPeriod?: string | null;
  // Legacy snake_case fields (kept for backward compat)
  offer_id?: string;
  type?: string;
  discount_value?: number;
  application_level?: string;
  order_types?: string[];
  location_scope?: string;
  location_ids?: string[];
  location_group_ids?: string[];
  is_exclusive?: boolean;
  is_hidden?: boolean;
  origin?: string;
  type_config?: unknown;
  eligibility?: unknown;
  promo_codes?: ApiPromoCode[];
  location_overrides?: unknown[];
  application_modes?: Record<string, string>;
  permitted_roles?: string[];
  manager_override_required?: boolean;
  redemption_count?: number;
  /** Number of times this offer has been applied/redeemed (revamp API). */
  offerAppliedCount?: number;
  version?: number;
  created_at?: string;
  createdAt?: string;
  updated_at?: string;
  updatedAt?: string;
  // Shared fields (both cases)
  name?: string;
  description?: unknown;
  internalNotes?: unknown;
  internal_notes?: string;
  discountValue?: number;
  status?: string;
  schedule?: unknown;
  noEndDate?: boolean;
  /** BOGO details when `offerType` is BOGO (revamp API). */
  bogo?: ApiBogoBlock | null;
}

export interface ApiOffersListResponse {
  locationId?: string;
  offers?: ApiOffer[];
  total?: number;
  totalActiveOffersCount?: number;
  totalRedemptions?: number;
  totalDiscountAmount?: number;
  totalOrderAmount?: number;
}

// ── Offers API ──

export async function getOfferByIdApi(
  offerId: string,
  locationId: string,
  token?: string | null,
): Promise<ApiOffer> {
  const url = `${REVAMP_BASE}/offers/${encodeURIComponent(offerId)}?location_id=${encodeURIComponent(locationId)}`;
  const res = await fetch(url, { headers: authHeaders(token) });
  if (!res.ok) throw new Error(`getOfferByIdApi: ${res.status} ${res.statusText}`);
  return res.json();
}

export async function getOffersApi(
  params: { locationId: string },
  token?: string | null,
): Promise<any> {
  const url = `${REVAMP_BASE}/api/v1/offers/get?locationId=${encodeURIComponent(params.locationId)}`;
  const res = await fetch(url, { headers: authHeaders(token) });
  if (!res.ok) throw new Error(`getOffersApi: ${res.status} ${res.statusText}`);
  return res.json();
}

export async function createOfferApi(
  payload: unknown,
  token?: string | null,
): Promise<ApiOffer> {
  const url = `${REVAMP_BASE}/api/v1/offers/create`;
  const hmacBody = await buildHmacBody(payload as Record<string, unknown>);
  const res = await fetch(url, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(hmacBody),
  });
  if (!res.ok) throw new Error(`createOfferApi: ${res.status} ${res.statusText}`);
  return res.json();
}

export async function updateOfferApi(
  offerId: string,
  payload: unknown,
  token?: string | null,
): Promise<ApiOffer> {
  const url = `${REVAMP_BASE}/api/v1/offers/update`;
  const bodyPayload =
    payload && typeof payload === 'object'
      ? { offerId, ...(payload as Record<string, unknown>) }
      : { offerId, payload };
  const hmacBody = await buildHmacBody(bodyPayload);
  const res = await fetch(url, {
    method: 'PATCH',
    headers: authHeaders(token),
    body: JSON.stringify(hmacBody),
  });
  if (!res.ok) throw new Error(`updateOfferApi: ${res.status} ${res.statusText}`);
  return res.json();
}

/** Revamp PATCH `saveMode` — backend expects ACTIVATE (not ACTIVE) when activating or resuming. */
export type OfferSaveMode = 'DRAFT' | 'ACTIVATE' | 'PAUSED' | 'ARCHIVED';

export function offerStatusToSaveMode(status: string): OfferSaveMode {
  if (status === 'active') return 'ACTIVATE';
  if (status === 'paused') return 'PAUSED';
  if (status === 'archived') return 'ARCHIVED';
  return 'DRAFT';
}

/** Revamp PATCH `/api/v1/offers/save-mode` — bulk-transitions multiple offers to a single saveMode (e.g. archive selected). */
export async function bulkArchiveOffersApi(
  offerIds: string[],
  token?: string | null,
): Promise<unknown> {
  const url = `${REVAMP_BASE}/api/v1/offers/save-mode`;
  const hmacBody = await buildHmacBody({ offerIds, saveMode: 'ARCHIVED' as OfferSaveMode });
  const res = await fetch(url, {
    method: 'PATCH',
    headers: authHeaders(token),
    body: JSON.stringify(hmacBody),
  });
  if (!res.ok) throw new Error(`bulkArchiveOffersApi: ${res.status} ${res.statusText}`);
  return res.json();
}

export async function deleteOfferApi(
  offerId: string,
  locationId: string,
  token?: string | null,
): Promise<void> {
  const base = `${REVAMP_BASE}/api/v1/offers/delete/`;
  const url = locationId
    ? `${base}?locationId=${encodeURIComponent(locationId)}`
    : base;
  const hmacBody = await buildHmacBody({ offerId, locationId });
  const res = await fetch(url, {
    method: 'DELETE',
    headers: authHeaders(token),
    body: JSON.stringify(hmacBody),
  });
  if (!res.ok) throw new Error(`deleteOfferApi: ${res.status} ${res.statusText}`);
}

/** Same `DELETE /api/v1/offers/delete/` endpoint, batched with `offerIds` for bulk-delete from the list page. */
export async function bulkDeleteOffersApi(
  offerIds: string[],
  locationId: string,
  token?: string | null,
): Promise<void> {
  const base = `${REVAMP_BASE}/api/v1/offers/delete/`;
  const url = locationId
    ? `${base}?locationId=${encodeURIComponent(locationId)}`
    : base;
  const hmacBody = await buildHmacBody({ offerIds, locationId });
  const res = await fetch(url, {
    method: 'DELETE',
    headers: authHeaders(token),
    body: JSON.stringify(hmacBody),
  });
  if (!res.ok) throw new Error(`bulkDeleteOffersApi: ${res.status} ${res.statusText}`);
}

// ── Promo Codes API ──

export async function getPromoCodesApi(
  offerId: string,
  locationId: string,
  token?: string | null,
): Promise<ApiPromoCode[]> {
  const url = `${REVAMP_BASE}/offers/${encodeURIComponent(offerId)}/promo-codes?location_id=${encodeURIComponent(locationId)}`;
  const res = await fetch(url, { headers: authHeaders(token) });
  if (!res.ok) throw new Error(`getPromoCodesApi: ${res.status} ${res.statusText}`);
  return res.json();
}

export async function createPromoCodeApi(
  offerId: string,
  payload: unknown,
  token?: string | null,
): Promise<ApiPromoCode> {
  const url = `${REVAMP_BASE}/offers/${encodeURIComponent(offerId)}/promo-codes`;
  const res = await fetch(url, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`createPromoCodeApi: ${res.status} ${res.statusText}`);
  return res.json();
}

export async function updatePromoCodeApi(
  offerId: string,
  promoCodeId: string,
  payload: unknown,
  token?: string | null,
): Promise<ApiPromoCode> {
  const url = `${REVAMP_BASE}/offers/${encodeURIComponent(offerId)}/promo-codes/${encodeURIComponent(promoCodeId)}`;
  const res = await fetch(url, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`updatePromoCodeApi: ${res.status} ${res.statusText}`);
  return res.json();
}

export async function deletePromoCodeApi(
  offerId: string,
  promoCodeId: string,
  locationId: string,
  token?: string | null,
): Promise<void> {
  const url = `${REVAMP_BASE}/api/v1/offers/${encodeURIComponent(offerId)}/promo-codes/${encodeURIComponent(promoCodeId)}?location_id=${encodeURIComponent(locationId)}`;
  const res = await fetch(url, {
    method: 'DELETE',
    headers: authHeaders(token),
  });
  if (!res.ok) throw new Error(`deletePromoCodeApi: ${res.status} ${res.statusText}`);
}
